import { isMarketReadyAtom, networkAtom } from "@/atoms/settings"
import { useCheckClient } from "@/providers/client"
import { Box, Chip, Container, Menu, MenuItem, Stack, Typography } from "@mui/material"
import { BlockchainNetwork, ConstantsHelper } from "@renec-foundation/relend-adapter"
import { useAtom, useAtomValue } from "jotai"
import { useCallback, useEffect, useRef, useState } from "react"

import KeyboardArrowDownIcon from "@/assets/icons/arrow-down.svg"
import RenecIcon from "@/assets/icons/icon-renec.svg"
import SolanaIcon from "@/assets/icons/icon-solana.svg"
import useMutableSearchParams from "@/hooks/useMutableSearchParams"
import useTrans from "@/hooks/useTrans"
import useWalletConnect from "@/hooks/useWalletConnect"
import Image from "next/image"

import useLocalStorage from "@/hooks/useLocalStorage"
import * as styles from "./styles"

const DynamicOverviewTitle = () => {
  const trans = useTrans()
  const [selectedMarket, setSelectedMarket] = useLocalStorage("selectedMarket", BlockchainNetwork.SOLANA.toLowerCase())
  const network = useAtomValue(networkAtom)
  const [selectedNetwork, setNetwork] = useAtom(networkAtom)
  const { isClient } = useCheckClient()
  const { params: queryParams, replaceState } = useMutableSearchParams()

  const { address, disconnectWallet, connectWallet } = useWalletConnect()
  const isMarketReady = useAtomValue(isMarketReadyAtom)

  const [anchorEl, setAnchorEl] = useState<Element | null>(null)
  const targetRef = useRef(null)
  const hasMarketTriggerClicked = useRef(false)

  const clickButton = useCallback(
    async (network: BlockchainNetwork) => {
      if (network === selectedNetwork) return

      if (address) {
        await disconnectWallet()
      }

      hasMarketTriggerClicked.current = true

      const market = network.toLowerCase()
      replaceState({ market })
      setSelectedMarket(market)

      connectWallet(network)
      setNetwork(network)
    },
    [address, connectWallet, disconnectWallet, selectedNetwork, setNetwork, replaceState, setSelectedMarket],
  )

  useEffect(() => {
    const marketParam = queryParams.market?.toLowerCase()
    if (!marketParam || marketParam === selectedMarket || hasMarketTriggerClicked.current) return

    const renecMarket = BlockchainNetwork.RENEC.toLowerCase()
    const solanaMarket = BlockchainNetwork.SOLANA.toLowerCase()
    const validMarkets = [renecMarket, solanaMarket]

    if (validMarkets.includes(marketParam)) {
      const selectedNetwork = marketParam === renecMarket ? BlockchainNetwork.RENEC : BlockchainNetwork.SOLANA
      clickButton(selectedNetwork)
    } else {
      clickButton(BlockchainNetwork.RENEC)
    }
  }, [clickButton, queryParams.market, selectedMarket])

  const handleDocsMenuHover = useCallback(() => {
    if (anchorEl !== targetRef.current) {
      setAnchorEl(targetRef.current)
    }
  }, [anchorEl])

  const handleDocsMenuClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  if (!isClient) {
    // Render a placeholder or nothing while waiting for the client to mount
    return <></>
  }

  return (
    <Container maxWidth='xl' className='mt-6'>
      <Stack
        direction='row'
        alignItems='center'
        gap={1}
        onClick={handleDocsMenuHover}
        data-testid='dashboard-overview-menu-button'
        className='relative w-fit cursor-pointer'
      >
        <Typography
          className='text-neutral-1000 font-montserrat text-2xl sm:text-[2rem] font-bold'
          data-testid='dashboard-overview-title'
        >
          {ConstantsHelper.isRenecChain(network)
            ? trans.dashboard.hero.renec_market
            : trans.dashboard.hero.solana_market}
        </Typography>
        <Box sx={styles.heroCarretWrapper}>
          <Box ref={targetRef}>
            <Image src={KeyboardArrowDownIcon} alt='market-icon' width={24} height={24} />
          </Box>
          <Menu
            id='dashboard-overview-menu'
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleDocsMenuClose}
            MenuListProps={{ onMouseLeave: handleDocsMenuClose, sx: styles.heroMenu }}
            disableScrollLock
            disableAutoFocusItem
            data-testid='dashboard-overview-menu'
            elevation={0}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Typography sx={styles.heroMenuSelectMenuTitle}>{trans.dashboard.hero.select_market}</Typography>
            <MenuItem
              onClick={() => {
                handleDocsMenuClose()
                clickButton(BlockchainNetwork.RENEC)
              }}
              sx={styles.heroMenuItemWrapper}
              data-testid='dashboard-overview-menu-renec'
              disabled={!isMarketReady}
            >
              <Image src={RenecIcon} alt='liquidations-icon' width={20} height={20} />
              <Typography sx={styles.heroMarketTitle}>Renec</Typography>
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleDocsMenuClose()
                clickButton(BlockchainNetwork.SOLANA)
              }}
              sx={styles.heroMenuItemWrapper}
              data-testid='dashboard-overview-menu-solana'
              disabled={!isMarketReady}
            >
              <Image src={SolanaIcon} alt='liquidations-icon' width={20} height={20} />
              <Typography sx={styles.heroMarketTitle}>Solana</Typography>
            </MenuItem>
          </Menu>
        </Box>
        <Chip
          variant='filled'
          label={trans.dashboard.hero.new}
          sx={styles.heroChip}
          className='absolute -top-3 -right-16'
          data-testid='dashboard-overview-chip-title'
        />
      </Stack>
    </Container>
  )
}

export default DynamicOverviewTitle
